/*
 * sd-form-tracking.js
 * Shared helper for appending visitor-tracking IDs to outbound Zoho form URLs:
 *   - gc_id   = Google Analytics client_id (via gtag 'get', falling back to the
 *               _ga first-party cookie if gtag isn't available/returns nothing)
 *   - op_id   = Ours Privacy visitor ID (first-party cookie `ours_device_id`)
 *   - gad_id  = Google Ads click id — gclid, or its privacy-restricted (iOS)
 *               fallbacks gbraid / wbraid when gclid is absent (priority:
 *               gclid > gbraid > wbraid). Only present as a URL param on the
 *               first page of the session, so this script captures it into
 *               sessionStorage on every page load and reuses it later. Omitted
 *               entirely when none was ever seen this session.
 *   - partnerID = partner-referral id from the landing URL (?partnerID=…).
 *               Captured on page load and persisted to both sessionStorage and
 *               localStorage so it survives navigation, then appended to every
 *               outbound form URL. Use getPartnerId() to build cart URLs that
 *               need it as the `attributes[partnerID]` cart-attribute instead.
 *
 * Usage:
 *   window.SDFormTracking.appendIds(url, function (finalUrl) { ... });
 *
 * The GA client_id lookup is asynchronous (gtag's callback), so consumers must
 * use the callback. A short timeout guarantees the callback always fires even
 * if gtag is slow or absent, so a redirect is never blocked.
 */
(function () {
  if (window.SDFormTracking) return;

  var GA_MEASUREMENT_ID = 'G-EJLVBZDLPF';
  var GTAG_TIMEOUT_MS = 800;
  var GAD_STORAGE_KEY = 'sd_gad_id';
  var PARTNER_STORAGE_KEY = 'sd_partner_id';

  /* Capture the Google Ads click id from the landing URL (first page of the
     session) and persist it. gclid is preferred; gbraid / wbraid are the
     privacy-restricted (e.g. iOS) fallbacks Google sends when gclid is absent. */
  try {
    var qp = new URLSearchParams(window.location.search);
    var landingAdId = qp.get('gclid') || qp.get('gbraid') || qp.get('wbraid');
    if (landingAdId) {
      window.sessionStorage.setItem(GAD_STORAGE_KEY, landingAdId);
    }
    /* Capture partnerID from the landing URL; persist to both stores so it
       survives the full session like the legacy insurance modal did. */
    var landingPartnerId = qp.get('partnerID');
    if (landingPartnerId) {
      window.sessionStorage.setItem(PARTNER_STORAGE_KEY, landingPartnerId);
      window.localStorage.setItem(PARTNER_STORAGE_KEY, landingPartnerId);
    }
  } catch { /* sessionStorage/URL access can throw; capture is best-effort */ }

  function getStoredAdId() {
    try {
      return window.sessionStorage.getItem(GAD_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  /* partnerID, freshest first: live URL → sessionStorage → localStorage. */
  function getPartnerId() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('partnerID');
      if (fromUrl) return fromUrl;
      return window.sessionStorage.getItem(PARTNER_STORAGE_KEY) ||
        window.localStorage.getItem(PARTNER_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(name + '=') === 0) {
        return parts[i].substring(name.length + 1);
      }
    }
    return '';
  }

  /* GA client_id from the _ga cookie ("GA1.1.<clientId>") — used as a fallback. */
  function clientIdFromCookie() {
    var ga = getCookie('_ga');
    if (!ga) return '';
    var v = ga.split('.');
    return v.length > 3 ? v[2] + '.' + v[3] : '';
  }

  function getClientId(cb) {
    var settled = false;
    function finish(id) {
      if (settled) return;
      settled = true;
      cb(id || clientIdFromCookie());
    }
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('get', GA_MEASUREMENT_ID, 'client_id', function (id) { finish(id); });
      } catch {
        finish('');
      }
      setTimeout(function () { finish(''); }, GTAG_TIMEOUT_MS);
    } else {
      finish('');
    }
  }

  function appendParam(url, key, value) {
    if (!value) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + key + '=' + encodeURIComponent(value);
  }

  window.SDFormTracking = {
    /* Append gc_id + op_id (+ gad_id / partnerID when present) to `url`, then
       cb(finalUrl). */
    appendIds: function (url, cb) {
      if (!url) { cb(url); return; }
      var opId = getCookie('ours_device_id');
      var gadId = getStoredAdId();
      var partnerId = getPartnerId();
      getClientId(function (gcId) {
        var out = appendParam(url, 'gc_id', gcId);
        out = appendParam(out, 'op_id', opId);
        out = appendParam(out, 'gad_id', gadId);
        out = appendParam(out, 'partnerID', partnerId);
        cb(out);
      });
    },

    /* Expose partnerID so cart-URL builders can add it as the
       `attributes[partnerID]` cart attribute (different shape than a query id). */
    getPartnerId: getPartnerId
  };
})();
